import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FadeUp } from "../components/Motion";
import PageMeta from "../components/PageMeta";
import { useSettings } from "../lib/useSettings";

const S = ({ title, children }) => (
  <section className="mt-12">
    <h2 className="font-serif-display text-2xl md:text-3xl text-[#F5F3EF] mb-4">{title}</h2>
    <div className="text-[#D8D6D0]/70 leading-relaxed space-y-4 text-base">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const s = useSettings();
  const email = s?.email || "northcrestedmonton@gmail.com";
  const phone = s?.phone_display || "(780) 655-7604";

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="privacy-policy-page" className="pt-32 pb-32">
      <PageMeta
        title="Privacy Policy — NorthCrest Landscaping"
        description="How NorthCrest Landscaping (Edmonton, Alberta) collects, uses, and protects your personal information. PIPEDA and Alberta PIPA compliant."
        path="/privacy-policy"
      />
      <div className="px-6 md:px-12 lg:px-16 max-w-4xl mx-auto">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Legal</div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-none">Privacy Policy</h1>
          <div className="overline text-[#D8D6D0]/50 mt-6">Last updated: June 2026</div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <p className="text-[#D8D6D0]/70 leading-relaxed mt-10 text-lg">
            NorthCrest Landscaping (&quot;NorthCrest&quot;, &quot;we&quot;, &quot;us&quot;) operates
            northcrestlandscaping.ca. This policy explains what personal information we collect, why we
            collect it, how we use and protect it, and the choices you have. We comply with Canada&apos;s
            Personal Information Protection and Electronic Documents Act (PIPEDA) and Alberta&apos;s
            Personal Information Protection Act (PIPA).
          </p>

          <S title="1. Information We Collect">
            <p><strong className="text-[#F5F3EF]">Information you provide directly:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Contact details — name, email address, phone number, and property address — when you submit a quote request, contact form, estimate request, or consultation booking.</li>
              <li>Project details you describe in messages, budget selections, and service preferences.</li>
              <li>Photos of your property that you voluntarily upload to our AI Yard Designer tool.</li>
            </ul>
            <p><strong className="text-[#F5F3EF]">Information collected automatically:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Usage data — pages visited, approximate location (city level), device and browser type — collected via cookies and similar technologies.</li>
              <li>Advertising analytics via Meta (Facebook) Pixel, which may set cookies to measure ad performance and website conversions.</li>
            </ul>
          </S>

          <S title="2. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to your enquiries and provide quotes, estimates, and consultations.</li>
              <li>To generate AI design concepts from photos you upload, at your request.</li>
              <li>To communicate about your project, appointments, and follow-ups.</li>
              <li>To improve our website, services, and marketing effectiveness.</li>
              <li>To comply with legal obligations.</li>
            </ul>
            <p>We do not sell, rent, or trade your personal information to third parties.</p>
          </S>

          <S title="3. Third-Party Services">
            <p>We use a small number of trusted service providers to operate the website:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-[#F5F3EF]">Meta (Facebook) Pixel</strong> — advertising measurement. See Meta&apos;s Privacy Policy for details on their data practices.</li>
              <li><strong className="text-[#F5F3EF]">AI image processing providers</strong> — photos uploaded to the AI Yard Designer are processed to generate design concepts and are not used to identify you.</li>
              <li><strong className="text-[#F5F3EF]">Email delivery providers</strong> — used to send you confirmations and responses to your enquiries.</li>
              <li><strong className="text-[#F5F3EF]">Hosting and database infrastructure</strong> — your data is stored on secured servers.</li>
            </ul>
            <p>These providers may process data outside of Canada (e.g., in the United States). We only share the minimum information necessary for each service to function.</p>
          </S>

          <S title="4. Cookies">
            <p>
              We use cookies for essential site functionality and advertising measurement (Meta Pixel). You can
              disable cookies in your browser settings; the website will continue to work, though some
              measurement features will be inactive. You can also opt out of Meta&apos;s ad targeting via your
              Facebook ad preferences.
            </p>
          </S>

          <S title="5. Data Retention">
            <p>
              We keep lead and project enquiry information for as long as needed to serve you and for reasonable
              business record-keeping. Uploaded yard photos and generated AI concepts are retained so you and our
              team can reference them during your project. You may request deletion of your data at any time (see
              Section 7).
            </p>
          </S>

          <S title="6. Security">
            <p>
              We use industry-standard safeguards — encrypted connections (HTTPS), access controls, and secured
              databases — to protect your information. No method of transmission or storage is 100% secure, but we
              take reasonable steps to protect your data against unauthorized access, disclosure, or loss.
            </p>
          </S>

          <S title="7. Your Rights">
            <p>Under PIPEDA and Alberta PIPA, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Withdraw consent and request deletion of your information.</li>
              <li>Ask questions or file a complaint about our privacy practices.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{" "}
              <a href={`mailto:${email}`} className="text-[#E2B85A] underline underline-offset-4">{email}</a>. We
              will respond within 30 days.
            </p>
          </S>

          <S title="8. Children's Privacy">
            <p>Our services are intended for adults. We do not knowingly collect personal information from individuals under 18.</p>
          </S>

          <S title="9. Changes to This Policy">
            <p>
              We may update this policy from time to time. The &quot;Last updated&quot; date at the top reflects the
              most recent revision. Material changes will be posted on this page.
            </p>
          </S>

          <S title="10. Contact Us">
            <p>
              NorthCrest Landscaping<br />
              Edmonton, Alberta, Canada<br />
              Email: <a href={`mailto:${email}`} className="text-[#E2B85A] underline underline-offset-4">{email}</a><br />
              Phone: {phone}
            </p>
            <p>
              Questions about this policy or our data practices? <Link to="/contact" className="text-[#E2B85A] underline underline-offset-4">Contact us</Link> — we&apos;re glad to help.
            </p>
          </S>
        </FadeUp>
      </div>
    </div>
  );
}
